import { useMemo } from "react";
import { useDebounce } from "@reactuses/core";
import { useLocation } from "@tanstack/react-router";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useServerTable } from "../../../components/table/tableFeatures";
import { MdRotateLeft } from "react-icons/md";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { resetTable, idsToVisibilityState } from "../../../components/table/utils";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { useStandardEntityMetrics } from "../../merchandising/components/tables/metricUtils";
import { Button } from "../../../components/core/basic/Button";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import TitleLink from "../../../components/core/basic/TitleLink";
import { useGetDSPLineItemPaginated } from "../hooks/useDSPLineItemPaginatedQuery";
import type { DSPLineItemRow } from "../../../zodios/apiDefinitions/api/dsp/lineItemDatasets";
import type { DSPOrder } from "../../../zodios/apiDefinitions/api/dsp/orderDatasets";
import LineItemExpander from "./LineItemExpander";
import useDSPSellerInfo from "../hooks/useDSPSellerInfo";
import { snakeCaseToSentenceCase, toTitleCase } from "../../../utils/formatText";
import getMetricSpec, { getFormatter } from "../../merchandising/utils/metrics";
import { GOAL_KPI_LOOKUP } from "../utils/constants";
import { useAppSelector } from "../../../redux/hooks";
import { useDateFormatter } from "../../../i18n/useDateFormatter";

const columnHelper = createColumnBuilder<DSPLineItemRow>();

const defaultSelectedColumns = [
  "dsp_sales",
  "dsp_total_sales",
  "dsp_ecpm",
  "dsp_impressions",
  "dsp_viewable_impressions",
  "dsp_viewability",
  "dsp_clicks",
  "dsp_cost",
  "dsp_total_purchases",
  "dsp_units",
];

type Props = {
  orderId: string | number;
  campaignData?: Partial<DSPOrder>;
};

const DSPLineItemsTable = ({ orderId, campaignData = {} }: Props) => {
  const { t } = useTranslation(["dsp", "common"]);
  const dateFmt = useDateFormatter("medium");
  const marketPlace = useAppSelector((reduxState) => reduxState.marketPlace);
  const { dspAdvertiser } = useDSPSellerInfo();
  const standardMetricIds = useStandardEntityMetrics("dspLineItem");

  const searchStr = useLocation({ select: (location) => location.searchStr });
  const queryString = useMemo(() => new URLSearchParams(searchStr).toString(), [searchStr]);

  const defaultColumnVisibility = useMemo(
    () =>
      idsToVisibilityState(
        ["name", "state", "start_date_time", "end_date_time", ...defaultSelectedColumns],
        [
          "name",
          "state",
          "delivery_status",
          "start_date_time",
          "end_date_time",
          "dsp_base_bid",
          "creation_date_time",
          "dsp_frequencies",
          "dsp_rotation_type",
          "goal",
          ...standardMetricIds,
        ],
      ),
    [standardMetricIds],
  );

  const { state, initialState, setters } = usePersistedTableState({
    persistKey: "dspLineItemsTable",
    initialState: {
      columnVisibility: defaultColumnVisibility,
      sorting: [{ id: "dsp_sales", desc: true }],
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        header: t(($) => $.dsp["Line Item"]),
        minSize: 250,
        size: 300,
        maxSize: 500,
        enableColumnFilter: false,
        cell: ({ row, getValue }) => {
          const lineItemId = row.original.ad_group_id;
          const programName = row.original.program_name;
          let url = `/user/dsp/${dspAdvertiser?.id}/lineitems/${lineItemId}`;
          if (queryString) {
            url = `${url}?${queryString}`;
          }

          return (
            <>
              <TitleLink to={url} title={getValue() ?? ""} />
              {programName && (
                <div>
                  <span
                    className={css({
                      borderRadius: "full",
                      paddingX: "small",
                      paddingY: "2px",
                      fontSize: "xs",
                      fontWeight: "semibold",
                      backgroundColor: "trellisPalette.3",
                      color: "white",
                    })}
                  >
                    {programName}
                  </span>
                </div>
              )}
            </>
          );
        },
        meta: {
          columnSelectorOptions: { label: t(($) => $.dsp["Line Item"]) },
        },
      }),
      columnHelper.accessor("state", {
        id: "state",
        header: t(($) => $.dsp["State"]),
        size: 140,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const isEnded = row.original.delivery_status === "ENDED";
          return <span>{isEnded ? t(($) => $.dsp["Ended"]) : toTitleCase(row.original.state ?? "")}</span>;
        },
      }),
      columnHelper.accessor("delivery_status", {
        id: "delivery_status",
        header: t(($) => $.dsp["Delivery Status"]),
        size: 170,
        cell: ({ getValue }) => toTitleCase(snakeCaseToSentenceCase(getValue() ?? "")),
      }),
      columnHelper.accessor("start_date_time", {
        id: "start_date_time",
        header: t(($) => $.dsp["Start Date"]),
        size: 170,
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? dateFmt.format(moment(value).toDate()) : "-";
        },
        meta: {
          columnFilterOptions: { format: "date" },
        },
      }),
      columnHelper.accessor("end_date_time", {
        id: "end_date_time",
        header: t(($) => $.dsp["End Date"]),
        size: 170,
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? dateFmt.format(moment(value).toDate()) : "-";
        },
        meta: {
          columnFilterOptions: { format: "date" },
        },
      }),
      columnHelper.accessor("base_bid", {
        id: "dsp_base_bid",
        header: t(($) => $.dsp["Base Bid"]),
        size: 120,
        cell: ({ getValue }) => {
          const val = getValue();
          return val != null ? `$${val.toFixed(2)}` : "-";
        },
      }),
      columnHelper.accessor("creation_date_time", {
        id: "creation_date_time",
        header: t(($) => $.dsp["Created On"]),
        size: 170,
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? dateFmt.format(moment(value).toDate()) : "-";
        },
        meta: {
          columnFilterOptions: { format: "date" },
        },
      }),
      columnHelper.display({
        id: "dsp_frequencies",
        header: t(($) => $.dsp["Frequency"]),
        size: 140,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const frequencies = row.original.frequencies;
          return <span>{frequencies.map((config) => toTitleCase(config.frequencyType)).join(", ")}</span>;
        },
      }),
      columnHelper.display({
        id: "dsp_rotation_type",
        header: t(($) => $.dsp["Creative Rotation Type"]),
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => <span>{toTitleCase(row.original.creative_rotation_type ?? "")}</span>,
      }),
      columnHelper.display({
        id: "goal",
        header: t(($) => $.dsp["Goal"]),
        size: 240,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const lineItem = row.original;
          const showGoalCompare = lineItem.state === "ACTIVE" && lineItem.delivery_status === "DELIVERING";
          const optimization = (campaignData as Record<string, unknown>).optimization as
            { goalSetting?: { kpi?: string; targetKpi?: number } } | undefined;

          const goalSpec = GOAL_KPI_LOOKUP[optimization?.goalSetting?.kpi ?? ""];
          if (!goalSpec) return null;

          const rawGoalTarget = optimization?.goalSetting?.targetKpi ?? 0;
          const goalTarget = goalSpec.format === "percent" ? rawGoalTarget / 100 : rawGoalTarget;
          const metricSpec = getMetricSpec(goalSpec.accessor);
          const formatter = getFormatter(marketPlace, metricSpec.format, metricSpec);

          const metricValueRaw =
            goalSpec.accessor in lineItem ? lineItem[goalSpec.accessor as keyof typeof lineItem] : null;
          const metricValue = typeof metricValueRaw === "number" ? metricValueRaw : 0;
          const onTarget = goalSpec.lower ? metricValue < goalTarget : metricValue > goalTarget;

          return (
            <span>
              <small>
                {t(($) => $.dsp["Actual {label}: {value}"], {
                  label: t(goalSpec.label),
                  value: showGoalCompare ? formatter(metricValue) : "-",
                })}
              </small>
              <br />
              <small>
                {showGoalCompare ?
                  <>
                    {onTarget ?
                      <span className={css({ color: "trellisPalette.12", fontWeight: "bold" })}>
                        {t(($) => $.dsp["On Target"])}
                      </span>
                    : <span className={css({ color: "trellisPalette.17", fontWeight: "bold" })}>
                        {t(($) => $.dsp["Off Target"])}
                      </span>
                    }
                  </>
                : null}{" "}
                <span>{t(($) => $.dsp["(vs. Goal: {value})"], { value: formatter(goalTarget) })}</span>
              </small>
            </span>
          );
        },
      }),
      ...standardMetricIds.map((id) => columnHelper.metric(id)),
    ],
    [campaignData, dateFmt, dspAdvertiser?.id, marketPlace, queryString, standardMetricIds, t],
  );

  const debouncedSearch = useDebounce(state.globalFilter, 500);

  const getQueryOptions = useGetDSPLineItemPaginated({ orderId });
  const lineItemsQuery = useBlockWindowQuery(getQueryOptions, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: state.columnFilters,
    search: debouncedSearch,
  });

  const tableData = useMemo<DSPLineItemRow[]>(() => {
    return (
      lineItemsQuery.data?.data.map((row) => {
        const formattedState = row.delivery_status === "ENDED" ? "ENDED" : row.state;
        return { ...row, state: formattedState };
      }) ?? []
    );
  }, [lineItemsQuery.data]);

  const table = useServerTable({
    data: tableData,
    columns,
    initialState,
    state,
    ...setters,
    rowCount: lineItemsQuery.data?.count ?? undefined,
    getRowId: (row) => String(row.ad_group_id ?? ""),
    getRowCanExpand: () => true,
    meta: {
      showSkeleton: lineItemsQuery.isFetching && (!lineItemsQuery.data || lineItemsQuery.isPlaceholderData),
    },
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions.Search)}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => <LineItemExpander row={row} />}
      />
    </>
  );
};

export default DSPLineItemsTable;
